import { normalizeGender } from "./genderNormalize";
import { forwardGeocodeAddress } from "./forwardGeocode";
import { INDIAN_CITIES, resolveIndianCity } from "../config/indianCities";

/**
 * Map patient gender to marketplace caregiver gender filter (male↔male, female↔female).
 * Returns null when patient gender is unknown / other (no forced filter).
 */
export function caregiverGenderFilterFromPatient(patient) {
  const g = normalizeGender(patient?.gender);
  if (g === "male" || g === "female") return g;
  return null;
}

function composeLine(parts = {}) {
  return [parts.houseNo, parts.street, parts.area, parts.city, parts.state, parts.pincode]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(", ");
}

function extractPatientAddressParts(patient) {
  const health =
    patient?.healthAssessment && typeof patient.healthAssessment === "object"
      ? patient.healthAssessment
      : {};
  const stored =
    health.addressParts && typeof health.addressParts === "object" ? health.addressParts : null;
  const line = String(patient?.address || "").trim();
  if (stored) {
    const composed = composeLine(stored);
    return {
      houseNo: String(stored.houseNo || "").trim(),
      street: String(stored.street || "").trim(),
      area: String(stored.area || "").trim(),
      city: String(stored.city || "").trim(),
      state: String(stored.state || "").trim(),
      pincode: String(stored.pincode || "").trim(),
      // Prefer the saved patient.address line when structured parts are empty/stale.
      line: composed || line,
    };
  }
  const segments = line
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  let pincode = "";
  if (segments.length && /^\d{6}$/.test(segments[segments.length - 1])) {
    pincode = segments.pop();
  }
  const parts = {
    houseNo: "",
    street: "",
    area: "",
    city: "",
    state: "",
    pincode,
    line,
  };
  if (segments.length >= 5) {
    parts.houseNo = segments[0] || "";
    parts.street = segments[1] || "";
    parts.area = segments.slice(2, segments.length - 2).join(", ");
    parts.city = segments[segments.length - 2] || "";
    parts.state = segments[segments.length - 1] || "";
  } else if (segments.length === 4) {
    parts.houseNo = segments[0] || "";
    parts.street = segments[1] || "";
    parts.area = segments[2] || "";
    parts.city = segments[3] || "";
  } else if (segments.length === 3) {
    parts.area = segments[0] || "";
    parts.city = segments[1] || "";
    parts.state = segments[2] || "";
  } else if (segments.length === 2) {
    parts.area = segments[0] || "";
    parts.city = segments[1] || "";
  } else if (segments.length === 1) {
    // Single token is usually a locality/area, not a city centre.
    parts.area = segments[0] || "";
  }
  return parts;
}

function normalizeAreaAlias(name) {
  const n = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\./g, "");
  if (n === "uday nagar" || n === "udayanagar") return "udaya nagar";
  if (n === "k r puram" || n === "krpuram" || n === "kr puram") return "kr puram";
  if (n === "a narayanapura" || n === "anarayanapura") return "a narayanapura";
  if (n === "indira nagar" || n === "indiranagar") return "indiranagar";
  if (n === "jp nagar" || n === "j p nagar" || n === "j.p nagar" || n === "jpnagar") {
    return "jp nagar";
  }
  if (n === "jayanagar" || n === "jaya nagar") return "jayanagar";
  return n;
}

function areaNameMatches(a, b) {
  const left = normalizeAreaAlias(a);
  const right = normalizeAreaAlias(b);
  return Boolean(left && right && (left === right || left.includes(right) || right.includes(left)));
}

function localityMentionedInHay(areaName, hay, parts) {
  const normalized = normalizeAreaAlias(areaName);
  if (!normalized) return false;
  if (hay.includes(normalized)) return true;
  if (areaNameMatches(areaName, parts.area)) return true;
  if (areaNameMatches(areaName, parts.street)) return true;
  // Allow "Indira Nagar" style spacing against catalog "Indiranagar".
  const compactHay = hay.replace(/\s+/g, "");
  const compactArea = normalized.replace(/\s+/g, "");
  return Boolean(compactArea && compactHay.includes(compactArea));
}

/**
 * Match patient address to city catalog.
 * IMPORTANT: never return a random locality just because the city matched
 * (that was snapping every Bengaluru address to Indiranagar).
 */
function matchCatalogLocation(parts, addressLine) {
  const area = String(parts.area || "").trim();
  const city = String(parts.city || "").trim();
  const state = String(parts.state || "").trim();
  const street = String(parts.street || "").trim();
  const hay = [addressLine, area, street, city, state]
    .filter(Boolean)
    .join(", ")
    .toLowerCase()
    .replace(/\s+/g, " ");

  // 1) Explicit structured area + city
  if (area && city) {
    const byArea = INDIAN_CITIES.find(
      (c) =>
        c.area &&
        areaNameMatches(c.area, area) &&
        String(c.city || "").toLowerCase() === city.toLowerCase().replace(/^bangalore$/, "bengaluru")
    );
    if (byArea) return { entry: byArea, precise: true };
  }

  // 2) Locality/area must actually appear in the address text.
  const localityHits = INDIAN_CITIES.map((c) => {
    if (!c.area) return null;
    if (!localityMentionedInHay(c.area, hay, parts)) return null;
    let score = 5;
    if (c.city && hay.includes(String(c.city).toLowerCase())) score += 2;
    if (c.city === "Bengaluru" && (hay.includes("bangalore") || hay.includes("bengaluru"))) score += 2;
    if (c.state && hay.includes(String(c.state).toLowerCase())) score += 1;
    if (areaNameMatches(c.area, area) || areaNameMatches(c.area, street)) score += 2;
    return { c, score };
  })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);

  if (localityHits[0]?.score >= 5) {
    return { entry: localityHits[0].c, precise: true };
  }

  // 3) Area-only address (e.g. "JP Nagar") — match locality without requiring city.
  if (area && !city) {
    const byAreaOnly = INDIAN_CITIES.find((c) => c.area && areaNameMatches(c.area, area));
    if (byAreaOnly) return { entry: byAreaOnly, precise: true };
  }

  // 4) City-level centre only (never a neighbourhood) when no locality matched.
  const cityStateLabel = [city, state].filter(Boolean).join(", ");
  const resolved =
    resolveIndianCity(cityStateLabel) ||
    resolveIndianCity(city) ||
    (area && city ? resolveIndianCity(`${area}, ${city}`) : null);
  if (resolved && !resolved.area) {
    return { entry: resolved, precise: false };
  }
  const cityOnly = INDIAN_CITIES.find(
    (c) =>
      !c.area &&
      city &&
      String(c.city || "").toLowerCase() === city.toLowerCase().replace(/^bangalore$/, "bengaluru")
  );
  if (cityOnly) return { entry: cityOnly, precise: false };

  return { entry: null, precise: false };
}

function buildGeocodeQueries(parts, addressLine) {
  const queries = [];
  const push = (q) => {
    const s = String(q || "").trim();
    if (s && !queries.includes(s)) queries.push(s);
  };
  push(addressLine);
  push(composeLine(parts));
  push(
    [parts.area, parts.city, parts.state, parts.pincode, "India"].filter(Boolean).join(", ")
  );
  push([parts.area, parts.city, "India"].filter(Boolean).join(", "));
  push([parts.city, parts.state, parts.pincode, "India"].filter(Boolean).join(", "));
  push([parts.city, parts.state, "India"].filter(Boolean).join(", "));
  push(parts.city ? `${parts.city}, India` : "");
  if (parts.area) push(`${parts.area}, India`);
  if (parts.pincode) push(`${parts.pincode}, India`);
  return queries;
}

async function geocodePatientAddress(parts, address) {
  for (const query of buildGeocodeQueries(parts, address)) {
    const geo = await forwardGeocodeAddress(query);
    if (geo) {
      return {
        latitude: geo.latitude,
        longitude: geo.longitude,
        locationLabel: geo.label || address || query,
        address: address || query,
        source: "geocode",
      };
    }
  }
  return null;
}

/**
 * Resolve search coords from patient address.
 * Order: precise catalog locality → geocode → city-centre catalog → search-bar fallback.
 */
export async function resolvePatientSearchLocation(patient, { fallback = null } = {}) {
  const parts = extractPatientAddressParts(patient);
  const address = parts.line || String(patient?.address || "").trim();
  if (!address && !parts.city && !parts.area) {
    const err = new Error("This patient has no address on file. Add an address in the patient profile.");
    err.code = "PATIENT_ADDRESS_MISSING";
    throw err;
  }

  const { entry: catalog, precise } = matchCatalogLocation(parts, address);
  if (catalog?.latitude != null && catalog?.longitude != null && precise) {
    return {
      latitude: Number(catalog.latitude),
      longitude: Number(catalog.longitude),
      locationLabel: catalog.label || address,
      address: address || catalog.label,
      source: "catalog",
    };
  }

  // When the patient named a locality we don't catalog (Tin Factory, JP Nagar, …),
  // geocode before accepting a city-centre pin — otherwise every Bengaluru address
  // was snapping to Indiranagar / Bengaluru centre.
  const geo = await geocodePatientAddress(parts, address);
  if (geo) return geo;

  if (catalog?.latitude != null && catalog?.longitude != null) {
    return {
      latitude: Number(catalog.latitude),
      longitude: Number(catalog.longitude),
      locationLabel: catalog.label || address,
      address: address || catalog.label,
      source: "catalog-city",
      usedFallback: Boolean(parts.area),
    };
  }

  const fbLat = Number(fallback?.latitude ?? fallback?.lat);
  const fbLng = Number(fallback?.longitude ?? fallback?.lng);
  if (Number.isFinite(fbLat) && Number.isFinite(fbLng)) {
    return {
      latitude: fbLat,
      longitude: fbLng,
      locationLabel: String(fallback.locationLabel || fallback.label || address || "Selected location"),
      address: address || String(fallback.locationLabel || fallback.label || ""),
      source: "fallback",
      usedFallback: true,
    };
  }

  const err = new Error(
    "Unable to locate this patient address on the map. Update the address or pick a city on the search bar."
  );
  err.code = "PATIENT_ADDRESS_UNRESOLVED";
  throw err;
}
